"use client";

import { useEffect, useRef, useState } from "react";

const services = [
  {
    id: "waste",
    color: "waste",
    icon: "🗑️",
    accentColor: "#EF4444",
    lightColor: "rgba(239,68,68,0.1)",
    borderColor: "rgba(239,68,68,0.15)",
    label: "Category 01",
    title: "Waste Removal",
    subtitle: "MARPOL-Compliant Disposal",
    desc: "Complete sludge, slop, bilge, and garbage removal from vessels — executed to strict MARPOL and IMO standards with zero environmental impact.",
    items: [
      "Sludge & Slop Discharge",
      "Bilge Water Removal",
      "Garbage & Condemned Stores",
      "All Tank Cleaning",
      "Pollution-Free Disposal",
    ],
    span: "lg:col-span-5",
    height: "min-h-[420px]",
  },
  {
    id: "supply",
    color: "supply",
    icon: "📦",
    accentColor: "#2196F3",
    lightColor: "rgba(33,150,243,0.1)",
    borderColor: "rgba(33,150,243,0.15)",
    label: "Category 02",
    title: "Supply Services",
    subtitle: "End-to-End Vessel Provisioning",
    desc: "From provisions to engine room stores, portable fire extinguishers to bunker supply — we stock everything your vessel needs at port.",
    items: [
      "Provision & Deck Stores",
      "Engine Room Stores",
      "Bunker Supply",
      "Portable Fire Extinguishers",
      "Machinery (New & Second-Hand)",
    ],
    span: "lg:col-span-7",
    height: "min-h-[420px]",
  },
  {
    id: "survey",
    color: "survey",
    icon: "🔍",
    accentColor: "#C8A84B",
    lightColor: "rgba(200,168,75,0.1)",
    borderColor: "rgba(200,168,75,0.15)",
    label: "Category 03",
    title: "Survey Services",
    subtitle: "Independent Marine Inspection",
    desc: "Accredited survey services covering cargo, vessel condition, insurance, and draught — providing independent verification your stakeholders trust.",
    items: [
      "Insurance & Draught Survey",
      "Break Bulk & Cargo Damage",
      "Hatch Fitness Inspection",
      "On/Off Hire Condition Survey",
      "Vessel Valuation Survey",
    ],
    span: "lg:col-span-12",
    height: "min-h-[280px]",
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-28 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-navy-800/30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-ocean-700/5 blur-[150px] pointer-events-none rounded-full" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 ${visible ? "animate-fade-up" : "opacity-0"}`}
        >
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-px bg-ocean-500" />
              <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">What We Do</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.9] tracking-tight">
              <span className="text-white">Three Core</span><br />
              <span className="text-gradient-ocean italic">Service Pillars</span>
            </h2>
          </div>
          <p className="text-[#8BA3BC] max-w-sm leading-relaxed text-[15px]">
            A comprehensive marine services portfolio designed as your single point of contact at every port.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {services?.map((service, i) => (
            <div
              key={service?.id}
              className={`${service?.span} ${service?.height} ${visible ? "animate-scale-in" : "opacity-0"} stagger-${i + 1}`}
            >
              <div
                className={`h-full rounded-3xl p-8 md:p-10 flex flex-col justify-between group hover-lift transition-all duration-500 relative overflow-hidden service-card-${service?.id}`}
                style={{
                  background: `linear-gradient(135deg, rgba(10,22,40,0.95), rgba(15,32,64,0.9))`,
                  border: `1px solid ${service?.borderColor}`,
                }}
              >
                {/* Glow background */}
                <div
                  className="absolute top-0 right-0 w-48 h-48 rounded-full blur-[60px] opacity-20 pointer-events-none transition-opacity duration-500 group-hover:opacity-40"
                  style={{ background: service?.accentColor }}
                />

                {/* Top content */}
                <div className="space-y-6 relative z-10">
                  <div className="flex items-start justify-between">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                      style={{ background: service?.lightColor, border: `1px solid ${service?.borderColor}` }}
                    >
                      {service?.icon}
                    </div>
                    <span className="text-[11px] uppercase tracking-widest font-bold" style={{ color: service?.accentColor }}>
                      {service?.label}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-display text-3xl md:text-4xl font-bold text-white">{service?.title}</h3>
                    <p className="text-[13px] font-semibold uppercase tracking-widest" style={{ color: service?.accentColor }}>
                      {service?.subtitle}
                    </p>
                  </div>

                  <p className="text-[#8BA3BC] leading-relaxed">{service?.desc}</p>
                </div>

                {/* Bottom: service items */}
                <div className="mt-8 space-y-2 relative z-10">
                  {service?.items?.map((item, j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: service?.accentColor }}
                      />
                      <span className="text-[13px] text-[#8BA3BC] font-medium">{item}</span>
                    </div>
                  ))}
                  <div className="pt-4">
                    <a
                      href="#contact"
                      className="inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-widest transition-colors group/link"
                      style={{ color: service?.accentColor }}
                    >
                      Request Service
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover/link:translate-x-1 transition-transform">
                        <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Services CTA */}
        <div className={`mt-12 flex justify-center ${visible ? "animate-fade-up" : "opacity-0"}`}>
          <a
            href="/services"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-ocean-500/40 text-ocean-400 hover:text-white hover:border-ocean-400 hover:bg-ocean-600/10 font-bold uppercase tracking-widest text-[13px] transition-all duration-300 hover:-translate-y-0.5"
          >
            View All Services & Pricing
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}